<script>
import { ref, provide } from 'vue';
// composable
import { useConfig } from 'dashboard/composables/useConfig';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAI } from 'dashboard/composables/useAI';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ForwardModal from './ForwardModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import EvolutionAPI from 'dashboard/api/evolution';
import WahaAPI from 'dashboard/api/waha';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  components: {
    MessageList,
    ReplyBox,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    ForwardModal,
    NextButton,
  },
  mixins: [inboxMixin],
  setup() {
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);
    const { isEnterprise } = useConfig();

    const keyboardEvents = {
      Escape: {
        action: () => {
          isPopOutReplyBox.value = false;
        },
      },
    };

    useKeyboardEvents(keyboardEvents);

    const {
      isAIIntegrationEnabled,
      isLabelSuggestionFeatureEnabled,
      fetchIntegrationsIfRequired,
      fetchLabelSuggestions,
    } = useAI();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      isEnterprise,
      isPopOutReplyBox,
      isAIIntegrationEnabled,
      isLabelSuggestionFeatureEnabled,
      fetchIntegrationsIfRequired,
      fetchLabelSuggestions,
      conversationPanelRef,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      connectionStatus: 'close',
      showForwardModal: false,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
      isSelectionMode: 'messageSelection/isSelectionMode',
      selectedMessageIds: 'messageSelection/getSelectedMessageIds',
      selectedCount: 'messageSelection/getSelectedCount',
    }),
    selectedMessages() {
      if (!this.isSelectionMode || this.selectedMessageIds.length === 0) {
        return [];
      }
      const messages = this.currentChat?.messages || [];
      return messages.filter(msg => this.selectedMessageIds.includes(msg.id));
    },
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.isEnterprise &&
        this.isAIIntegrationEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        if (
          ['evolution', 'waha', 'uazapi'].includes(this.whatsAppAPIProvider)
        ) {
          return '';
        }
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (['evolution', 'waha', 'uazapi'].includes(this.whatsAppAPIProvider)) {
        return '';
      }
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (['evolution', 'waha', 'uazapi'].includes(this.whatsAppAPIProvider)) {
        return '';
      }
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    showConnectionBanner() {
      return this.connectionStatus === 'close';
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
    conversationPanelClasses() {
      return 'conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4';
    },
    whatsappBackgroundStyle() {
      // Background image removed - using clean background instead
      return {};
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.fetchConnectionStatus();
    },
    inbox(newInbox, oldInbox) {
      if (newInbox && newInbox !== oldInbox) {
        this.fetchConnectionStatus();
      }
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a new message comes in, we refetch the label suggestions
    emitter.on(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS, this.fetchSuggestions);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
    emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, this.onConnectionUpdate);
    this.fetchConnectionStatus();
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, this.onConnectionUpdate);
  },

  methods: {
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      if (!this.isEnterprise) {
        return;
      }

      // method available in mixin, need to ensure that integrations are present
      await this.fetchIntegrationsIfRequired();

      if (!this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.fetchLabelSuggestions({
        conversationId: this.currentChat.id,
      });

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      if (this.currentChat?.id) {
        this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
      }
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          messageElement.classList.add('bg-n-brand');
          setTimeout(() => {
            messageElement.classList.remove('bg-n-brand');
          }, 1000);
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          const oldestRealMessage = this.currentChat.messages.find(
            msg => typeof msg.id === 'number'
          );
          const params = { conversationId: this.currentChat.id };
          if (oldestRealMessage) {
            params.before = oldestRealMessage.id;
          }
          await this.$store.dispatch('fetchPreviousMessages', params);
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },

    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },

    onConnectionUpdate(payload) {
      if (payload.inbox_id === this.inboxId) {
        this.connectionStatus = payload.status;
      }
    },

    async fetchConnectionStatus() {
      const {
        channel_type: channelType,
        channel = {},
        provider_config: providerConfig,
      } = this.inbox || {};

      const evolutionLikeProviders = ['evolution', 'waha', 'uazapi'];

      if (
        channelType !== 'Channel::Whatsapp' ||
        !evolutionLikeProviders.includes(channel.provider)
      ) {
        this.connectionStatus = null;
        return;
      }

      if (!providerConfig) {
        this.connectionStatus = null;
        return;
      }

      try {
        const config = this.inbox.provider_config || {};

        if (channel.provider === 'waha') {
          const sessionName = config.session_name || config.instance_name;
          if (!sessionName) {
            this.connectionStatus = null;
            return;
          }
          const sessions = await WahaAPI.listSessions(
            config.api_url,
            config.admin_token,
            { includeStopped: true }
          );
          const session = Array.isArray(sessions)
            ? sessions.find(item => {
                return (
                  item.name === sessionName || item.session === sessionName
                );
              })
            : null;
          if (session) {
            this.connectionStatus =
              session.connectionStatus || session.state || session.status;
          } else {
            this.connectionStatus = 'close';
          }
        } else if (channel.provider === 'uazapi') {
          this.connectionStatus =
            this.inbox.provider_connection?.connection || null;
        } else {
          const instances = await EvolutionAPI.fetchInstances(
            config.api_url,
            config.admin_token
          );
          const instance = instances.find(inst => {
            return (
              inst.name === config.instance_name ||
              inst.instanceName === config.instance_name
            );
          });

          if (instance) {
            this.connectionStatus =
              instance.connectionStatus || instance.state || instance.status;
          } else {
            this.connectionStatus = 'close';
          }
        }
      } catch (e) {
        // Ignore errors and keep the existing status
      }
    },

    goToInboxSettings() {
      this.$router.push({
        name: 'settings_inbox_show',
        params: { accountId: this.currentAccountId, inboxId: this.inboxId },
      });
    },
    cancelSelection() {
      this.$store.dispatch('messageSelection/exitSelectionMode');
    },
    openForwardModalWithSelection() {
      if (this.selectedCount > 0) {
        this.showForwardModal = true;
      }
    },
    onCloseForwardModal() {
      this.showForwardModal = false;
      this.$store.dispatch('messageSelection/exitSelectionMode');
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <Banner
      v-if="showConnectionBanner"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('INBOX_MGMT.RECONNECTION_REQUIRED')"
      has-action-button
      :action-button-label="$t('INBOX_MGMT.CLICK_TO_RECONNECT')"
      @primary-action="goToInboxSettings"
    />
    <Banner
      v-if="
        !currentChat.can_reply &&
        !['evolution', 'waha', 'uazapi'].includes(whatsAppAPIProvider)
      "
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <Banner
      v-else-if="hasDuplicateInstagramInbox"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
    />
    <div
      class="flex-shrink flex-grow basis-px overflow-hidden"
      :style="whatsappBackgroundStyle"
    >
      <MessageList
        ref="conversationPanelRef"
        :class="conversationPanelClasses"
        :current-user-id="currentUserId"
        :first-unread-id="unReadMessages[0]?.id"
        :is-an-email-channel="isAnEmailChannel"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :messages="getMessages"
        @retry="handleMessageRetry"
      >
        <template #beforeAll>
          <transition name="slide-up">
            <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
            <li
              class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
            >
              <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
            </li>
          </transition>
        </template>
        <template #unreadBadge>
          <li
            v-show="unreadMessageCount != 0"
            class="list-none flex justify-center items-center"
          >
            <span
              class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
            >
              {{ unreadMessageLabel }}
            </span>
          </li>
        </template>
        <template #after>
          <ConversationLabelSuggestion
            v-if="shouldShowLabelSuggestions"
            :suggested-labels="labelSuggestions"
            :chat-labels="currentChat.labels"
            :conversation-id="currentChat.id"
          />
        </template>
      </MessageList>
    </div>
    <div
      class="flex relative flex-col"
      :class="{
        'modal-mask': isPopOutReplyBox,
        'bg-n-solid-2': !isPopOutReplyBox,
      }"
    >
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <ReplyBox
        v-if="!isSelectionMode"
        :pop-out-reply-box="isPopOutReplyBox"
        @update:pop-out-reply-box="isPopOutReplyBox = $event"
      />
      <!-- Selection mode action bar -->
      <div
        v-if="isSelectionMode"
        class="flex items-center justify-between gap-4 px-4 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm text-slate-600 dark:text-slate-400">
            {{
              $t('CONVERSATION.FORWARD_MODAL.MESSAGES_SELECTED', {
                count: selectedCount,
              })
            }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <NextButton ghost slate sm @click="cancelSelection">
            {{ $t('CONVERSATION.FORWARD_MODAL.CANCEL_SELECTION') }}
          </NextButton>
          <NextButton
            sm
            :disabled="selectedCount === 0"
            class="bg-woot-500 hover:bg-woot-600 text-white"
            @click="openForwardModalWithSelection"
          >
            {{ $t('CONVERSATION.FORWARD_MODAL.FORWARD') }}
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span v-if="selectedCount > 0" class="ml-1">
              ({{ selectedCount }})
            </span>
          </NextButton>
        </div>
      </div>
    </div>
    <!-- Forward Modal for multiple messages -->
    <ForwardModal
      v-if="showForwardModal"
      v-model:show="showForwardModal"
      :messages="selectedMessages"
      @close="onCloseForwardModal"
    />
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed;

  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-n-weak max-w-[75rem] w-[70%];

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute ltr:left-auto rtl:right-auto bottom-1;
    }
  }
}
</style>
